/**
 * Stream-text helpers for the chat hook (useChat): chunk concatenation,
 * sentence-boundary normalization, status-visibility pacing, activity-key
 * dedup/IDs, and the stream-enter-attr strip seam.
 *
 * Extracted verbatim from useChat.ts (PR6 — pure-utils phase). No behavior change
 * (sed-lifted byte-for-byte). isStrayPageFinalText is an intentional mirror of
 * apps/x-agent/src/routes/chat.ts — kept duplicated (web cannot import x-agent).
 */

const MIN_XAVIOR_STATUS_VISIBLE_MS = 700

export function appendAssistantChunk(
  existing: string,
  incoming: string,
  options?: { forceParagraphBreak?: boolean },
): string {
  if (!incoming) return existing
  if (!existing) return incoming

  let next = incoming
  if (options?.forceParagraphBreak) {
    const trimmed = next.trimStart()
    if (!trimmed) return existing
    return `${existing}\n\n${trimmed}`
  }

  const previousChar = existing[existing.length - 1] ?? ''
  const nextChar = next[0] ?? ''
  const missingBoundarySpace =
    previousChar !== ''
    && nextChar !== ''
    && !/\s/.test(previousChar)
    && !/\s/.test(nextChar)

  // If a hidden directive was removed between sentences, keep a natural boundary.
  if (missingBoundarySpace && /[.!?]/.test(previousChar) && /[A-Z]/.test(nextChar)) {
    next = ` ${next}`
  }

  return existing + next
}

export function normalizeAssistantSentenceBoundaries(text: string): string {
  if (!text) return text
  return text.replace(
    /([a-z0-9\)])\s+(It's|It’s|It is|They're|They’re|They are|This|That|These|Those)\b/g,
    '$1. $2',
  )
}

export async function waitForXaviorStatusVisibility(lastStatusAtMs: number, signal: AbortSignal): Promise<void> {
  if (!lastStatusAtMs) return
  const waitMs = MIN_XAVIOR_STATUS_VISIBLE_MS - (Date.now() - lastStatusAtMs)
  if (waitMs <= 0) return

  await new Promise<void>((resolve) => {
    const timeout = setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, waitMs)

    const onAbort = () => {
      clearTimeout(timeout)
      signal.removeEventListener('abort', onAbort)
      resolve()
    }

    signal.addEventListener('abort', onAbort, { once: true })
  })
}

export function isGenericThinkingLabel(label: string | null | undefined): boolean {
  const normalized = (label || '').trim().toLowerCase()
  return normalized === 'thinking' || normalized === 'thinking...'
}

/** Labels that are too vague to justify replacing a more informative status. */
export function isUninformativeToolLabel(label: string | null | undefined): boolean {
  const normalized = (label || '').trim().toLowerCase()
  return /^(running|working|preparing)\.\.\.$/.test(normalized)
}

/**
 * Mirror of isStrayPageFinalText in apps/x-agent/src/routes/chat.ts.
 * When a webpage build's closing sentence gets chewed by the planning-chatter
 * filter, a trailing adverb/connective can survive as the only visible text —
 * "throughout", "also", etc. Reject those here too so a mid-route server that
 * didn't filter still doesn't render them to the user.
 */
const STRAY_PAGE_FINAL_WORDS = new Set<string>([
  'throughout', 'also', 'and', 'but', 'however', 'meanwhile',
  'therefore', 'overall', 'finally', 'additionally', 'ultimately',
  'anyway', 'so', 'thus', 'here', 'this',
])
const GOOD_SHORT_CLOSINGS = new Set<string>([
  'done', 'saved', 'restored', 'built', 'live', 'updated', 'reverted',
])
export function isStrayPageFinalText(text: string): boolean {
  const trimmed = text.trim()
  if (!trimmed) return true
  const normalized = trimmed.replace(/[.!?,;:]+$/g, '').toLowerCase()
  if (!normalized) return true
  if (STRAY_PAGE_FINAL_WORDS.has(normalized)) return true
  if (!/\s/.test(normalized) && normalized.length < 20) {
    return !GOOD_SHORT_CLOSINGS.has(normalized)
  }
  return false
}

/**
 * Normalize activity labels for phrase-level dedup. Strips articles,
 * the word "section", punctuation, and whitespace variation so that
 * "Building header...", "Building the header...", and
 * "Building Header section..." collapse to one timeline entry.
 */
export function normalizeActivityKey(label: string): string {
  return (label || '')
    .trim()
    .toLowerCase()
    .replace(/\b(the|a|an)\b/g, '')
    .replace(/\bsection\b/g, '')
    .replace(/["'.,!?:;()]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

let _activityCounter = 0
export function makeActivityId(): string {
  return `act-${Date.now()}-${++_activityCounter}`
}

/**
 * Strip the transient `data-xavior-stream-enter` marker from a cumulative
 * HTML string before persistence. The attribute is stamped on section frames
 * by the client so the iframe can run a fade-in transition, then removed by
 * the iframe's inspector script on the next rAF tick. If we ever save the
 * raw pre-stripped HTML and reload the page, every section would render
 * invisibly — belt-and-suspenders strip it here too.
 */
export function stripStreamEnterAttr(html: string): string {
  if (!html) return html
  // Remove attribute variants: `data-xavior-stream-enter`, possibly with
  // surrounding whitespace, quoted empty value, or trailing whitespace.
  return html.replace(/\s+data-xavior-stream-enter(?:="[^"]*")?/g, '')
}
